import React, { useEffect, useState, useCallback, useRef } from 'react';
import '../super-admin.css';
import { 
  Typography, Card, Button, message, Row, Col, 
  Table, Tag, Space, Modal, Form, Input, Statistic,
  Tooltip, Divider, Alert, Select,
  Badge
} from 'antd';
import { 
  KeyOutlined, ApiOutlined, CopyOutlined, DeleteOutlined,
  PlusOutlined, ReloadOutlined, SecurityScanOutlined, MonitorOutlined, EditOutlined
} from '@ant-design/icons';
import { API_BASE, authHeaders } from '../../config/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const SuperAdminAPIPortal = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [users, setUsers] = useState([]);
  const [usage, setUsage] = useState({
    total_keys: 0,
    active_keys: 0,
    total_requests_today: 0,
    avg_requests_per_key: 0
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [filterUser, setFilterUser] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [form] = Form.useForm();
  const [importForm] = Form.useForm();
  const initialLoadRef = useRef(true);

  // Load all API keys
  const loadApiKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_BASE}/admin/api-keys?`;
      if (filterUser) url += `user_id=${filterUser}&`;
      if (filterStatus) url += `status=${filterStatus}&`;
      
      const res = await fetch(url, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load API keys');
      const data = await res.json();
      
      setApiKeys(data.keys || []);
    } catch (err) {
      console.error('Error loading API keys:', err);
      setError('Failed to load API keys');
      message.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [filterUser, filterStatus]);

  // Load users for filtering
  const loadUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/users`, {
        headers: { ...authHeaders() }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  }, []);

  // Load usage statistics
  const loadUsage = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/api-usage`, {
        headers: { ...authHeaders() }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUsage(data);
      }
    } catch (err) {
      console.error('Error loading usage:', err);
    }
  }, []);

  // Generate new API key for user
  const generateApiKey = async (values) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify(values)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to generate API key');
      }

      const newApiKey = await res.json();
      setNewKey(newApiKey);
      setModalVisible(true);
      form.resetFields();
      loadApiKeys();
      message.success('API key generated successfully');
    } catch (err) {
      message.error(err.message || 'Failed to generate API key');
    } finally {
      setLoading(false);
    }
  };

  // Revoke API key
  const revokeApiKey = async (keyId) => {
    try {
      const res = await fetch(`${API_BASE}/admin/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to revoke API key');
      }

      message.success('API key revoked successfully');
      loadApiKeys();
    } catch (err) {
      message.error(err.message || 'Failed to revoke API key');
    }
  };

  // Update API key
  const updateApiKey = async (values) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/api-keys/${editingKey.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify(values)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update API key');
      }

      message.success('API key updated successfully');
      setEditingKey(null);
      form.resetFields();
      loadApiKeys();
    } catch (err) {
      message.error(err.message || 'Failed to update API key');
    } finally {
      setLoading(false);
    }
  };

  // Import third-party API key
  const importThirdPartyKey = async (values) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/third-party-keys/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify(values)
      });

      if (!res.ok) throw new Error('Failed to import third-party API key');

      await res.json(); // Consume the response
      message.success('Third-party API key imported successfully');
      setImportModalVisible(false);
      importForm.resetFields();
      loadApiKeys();
    } catch (err) {
      message.error(err.message || 'Failed to import third-party API key');
    } finally {
      setLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label} copied to clipboard`);
    }).catch(() => {
      message.error('Failed to copy to clipboard');
    });
  };

  useEffect(() => {
    if (initialLoadRef.current) {
      loadApiKeys();
      loadUsers();
      loadUsage();
      initialLoadRef.current = false;
    }
  }, [loadApiKeys, loadUsers, loadUsage]);

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => {
        const userName = record.user_name || record.user_email?.split('@')[0] || 'Unknown';
        const userEmail = record.user_email || 'No email';
        return (
          <div>
            <Text strong>{userName}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>{userEmail}</Text>
          </div>
        );
      },
    },
    {
      title: 'Key Name',
      dataIndex: 'key_name',
      key: 'key_name',
      render: (name) => <Text strong>{name}</Text>
    },
    {
      title: 'API Key',
      dataIndex: 'masked_key',
      key: 'key',
      render: (text) => (
        <Text code style={{ fontSize: 12 }}>
          {text}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Usage Today',
      dataIndex: 'usage_today',
      key: 'usage_today',
      render: (usage) => (
        <Badge count={usage || 0} showZero style={{ backgroundColor: '#52c41a' }} />
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Key">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingKey(record);
                form.setFieldsValue({
                  key_name: record.key_name,
                  user_id: record.user_id,
                  status: record.status
                });
              }}
            />
          </Tooltip>
          <Tooltip title="Revoke Key">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Revoke API Key',
                  content: `Are you sure you want to revoke "${record.key_name}"? This action cannot be undone.`,
                  okText: 'Revoke',
                  okType: 'danger',
                  onOk: () => revokeApiKey(record.id),
                });
              }}
              disabled={record.status !== 'active'}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="sa-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>API Portal Management</Title>
        <Space>
          <Button 
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setImportModalVisible(true)}
          >
            Import Third-Party Key
          </Button>
          <Button 
            icon={<ReloadOutlined />}
            onClick={() => {
              loadApiKeys();
              loadUsage();
            }}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Error Display */}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Usage Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Total Keys"
              value={usage.total_keys}
              prefix={<KeyOutlined style={{ color: '#3730a3' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Active Keys"
              value={usage.active_keys}
              prefix={<ApiOutlined style={{ color: '#059669' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Requests Today"
              value={usage.total_requests_today}
              prefix={<MonitorOutlined style={{ color: '#d97706' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Avg Requests/Key"
              value={usage.avg_requests_per_key}
              precision={1}
              prefix={<SecurityScanOutlined style={{ color: '#7c3aed' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* API Keys Management */}
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <KeyOutlined />
            <span>All API Keys</span>
          </div>
        }
        extra={
          <Space>
            <Select
              allowClear
              placeholder="Filter by user"
              style={{ width: 200 }}
              value={filterUser}
              onChange={setFilterUser}
            >
              {users.map(user => (
                <Option key={user.id} value={user.id}>
                  {user.name || user.email?.split('@')[0]} ({user.email})
                </Option>
              ))}
            </Select>
            <Select
              allowClear
              placeholder="Filter by status"
              style={{ width: 120 }}
              value={filterStatus}
              onChange={setFilterStatus}
            >
              <Option value="active">Active</Option>
              <Option value="revoked">Revoked</Option>
            </Select>
            <Form
              form={form}
              layout="inline"
              onFinish={editingKey ? updateApiKey : generateApiKey}
            >
              <Form.Item
                name="key_name"
                rules={[{ required: true, message: 'Please enter key name' }]}
              >
                <Input placeholder="Key name" style={{ width: 200 }} />
              </Form.Item>
              <Form.Item
                name="user_id"
                rules={[{ required: true, message: 'Please select user' }]}
              >
                <Select placeholder="Select user" style={{ width: 200 }}>
                  {users.map(user => (
                    <Option key={user.id} value={user.id}>
                      {user.name || user.email?.split('@')[0]} ({user.email})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  icon={editingKey ? <EditOutlined /> : <PlusOutlined />}
                  loading={loading}
                >
                  {editingKey ? 'Update' : 'Generate'}
                </Button>
              </Form.Item>
              {editingKey && (
                <Form.Item>
                  <Button onClick={() => {
                    setEditingKey(null);
                    form.resetFields();
                  }}>
                    Cancel
                  </Button>
                </Form.Item>
              )}
            </Form>
          </Space>
        }
        style={{ borderRadius: 14 }}
      >
        <Table
          columns={columns}
          dataSource={apiKeys}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${total} API keys` }}
          locale={{
            emptyText: 'No API keys found. Generate your first key to get started.'
          }}
        />
      </Card>

      {/* New Key Modal */}
      <Modal
        title="API Key Generated"
        open={modalVisible && newKey}
        onCancel={() => {
          setModalVisible(false);
          setNewKey(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setModalVisible(false);
            setNewKey(null);
          }}>
            Close
          </Button>
        ]}
        width={700}
      >
        {newKey && (
          <div>
            <Alert
              message="Important: Save these credentials now"
              description="For security reasons, you won't be able to see the full API key or signing secret again."
              type="warning"
              style={{ marginBottom: 16 }}
            />
            
            <div style={{ marginBottom: 16 }}>
              <Text strong>API Key:</Text>
              <div style={{ 
                background: '#f5f5f5', 
                padding: 12, 
                borderRadius: 6, 
                fontFamily: 'monospace',
                fontSize: 12,
                marginTop: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>{newKey.api_key}</span>
                <Button 
                  type="text" 
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(newKey.api_key, 'API Key')}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>Signing Secret (for HMAC):</Text>
              <div style={{ 
                background: '#f5f5f5', 
                padding: 12, 
                borderRadius: 6, 
                fontFamily: 'monospace',
                fontSize: 12,
                marginTop: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>{newKey.signing_secret}</span>
                <Button 
                  type="text" 
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(newKey.signing_secret, 'Signing Secret')}
                />
              </div>
            </div>

            <Divider />
            
            <Title level={5}>HMAC Signature Example (Node.js):</Title>
            <TextArea
              value={`const crypto = require('crypto');

const payload = JSON.stringify({
  event_type: "page_view",
  website_url: "https://example.com"
});

const signature = crypto
  .createHmac('sha256', '${newKey.signing_secret}')
  .update(payload)
  .digest('hex');

// Include in X-Signature header`}
              rows={8}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
              readOnly
            />
          </div>
        )}
      </Modal>

      {/* Import Third-Party API Key Modal */}
      <Modal
        title="Import Third-Party API Key"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          importForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={importForm}
          layout="vertical"
          onFinish={importThirdPartyKey}
        >
          <Form.Item
            name="user_id"
            label="Select User"
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select placeholder="Choose a user to import this key for">
              {users.map(user => (
                <Option key={user.id} value={user.id}>
                  {user.name || user.email?.split('@')[0]} ({user.email})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="website_id"
            label="Select Website"
            rules={[{ required: true, message: 'Please select a website' }]}
          >
            <Select placeholder="Choose a website">
              {/* This would need to be populated with user's websites */}
              <Option value="1">example.com</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="provider"
            label="Platform Name"
            rules={[{ required: true, message: 'Please specify the platform name' }]}
          >
            <Select placeholder="Choose platform">
              <Option value="shopify">Shopify</Option>
              <Option value="amazon">Amazon</Option>
              <Option value="google">Google Analytics</Option>
              <Option value="facebook">Facebook</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="key_name"
            label="Key Name"
            rules={[{ required: true, message: 'Please enter a key name' }]}
          >
            <Input placeholder="e.g., Shopify Analytics API" />
          </Form.Item>

          <Form.Item
            name="api_key"
            label="API Key"
            rules={[{ required: true, message: 'Please enter the API key' }]}
          >
            <TextArea 
              rows={3} 
              placeholder="Paste the third-party API key here"
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (Optional)"
          >
            <TextArea 
              rows={2} 
              placeholder="Describe what this API key is used for"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => {
                  setImportModalVisible(false);
                  importForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
              >
                Import Key
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SuperAdminAPIPortal;
